import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export type InstitutionRole = 
  | "super_admin" 
  | "compliance_officer" 
  | "athletic_director" 
  | "coach";

export const useUserRole = () => {
  const { user } = useAuth();

  const { data: roles, isLoading } = useQuery({
    queryKey: ["user-roles", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user!.id);

      if (error) throw error;
      return (data || []).map((r: any) => r.role as InstitutionRole);
    },
  });

  const hasRole = (role: InstitutionRole): boolean => {
    return roles?.includes(role) ?? false;
  };

  const hasAnyRole = (checkRoles: InstitutionRole[]): boolean => {
    return checkRoles.some((role) => hasRole(role));
  };

  const isAdmin = (): boolean => {
    return hasAnyRole(["super_admin", "compliance_officer"]);
  };

  const primaryRole = (): InstitutionRole | null => {
    if (!roles || roles.length === 0) return null;
    // Priority order
    if (hasRole("super_admin")) return "super_admin";
    if (hasRole("compliance_officer")) return "compliance_officer";
    if (hasRole("athletic_director")) return "athletic_director";
    if (hasRole("coach")) return "coach";
    return roles[0];
  };

  return {
    roles: roles ?? [],
    hasRole,
    hasAnyRole,
    isAdmin,
    primaryRole,
    isLoading,
  };
};
